import { Component, Input, OnInit, OnDestroy, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { DatePipe } from '@angular/common';
import { EpahafService } from '../epahaf.service';
import { PdfService } from '../epahaf-pdf.service';
import * as _ from 'lodash';

export interface PahafMemberData {
  pcpName?: string;
  memberHmoID?: string;
  memberFirstName?: string;
  memberDOB?: string;
  status?: string;
  refreshStatus?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-pahaf-bulk-download',
  templateUrl: './pahaf-bulk-download.component.html',
  styleUrls: ['../epahaf.component.scss', './pahaf-bulk-download.component.css'],
  standalone: false
})
export class PahafBulkDownloadComponent implements OnInit, OnDestroy, OnChanges {
  @Input() memberData: PahafMemberData[] = [];
  
  displayedColumns: string[] = ['select', 'pcpName', 'memberName', 'memberDOB', 'status', 'refreshStatus'];
  dataSource: MatTableDataSource<PahafMemberData>;
  selection = new SelectionModel<PahafMemberData>(true, []);
  
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  pahafStatusOptions = [
    { id: 5, itemName: 'Not Received' },
    { id: 7, itemName: 'Portal In Progress' },
    { id: 3, itemName: 'Denied' },
    { id: 6, itemName: 'On Hold - Missing claim' },
    { id: 8, itemName: 'On Hold - Non-AWV Claim' },
    { id: 1, itemName: 'Pending Coder Review' },
    { id: 2, itemName: 'Payment Approved' },    
    { id: 4, itemName: 'Paid' }
  ];

  refreshStatusOptions = [
    { id: 1, itemName: 'New Patient Form' },
    { id: 3, itemName: 'Conditions Added' },
    { id: 2, itemName: 'No Conditions Added' }
  ];

  selectedPahafStatus: any[] = [];
  selectedRefreshStatus: any[] = [];
  selectedPcp: any[] = []; 
  isSecondDropdownDisabled: boolean = true;
  isDownloading: boolean = false;
  uniquePCP: any =[];

  pcpOptions: any = [];
  
  private cachedBulkPahafData: any[] = null;
  private cachedCodingConstant: any[] = null;
  private isCacheInitialized: boolean = false;
  
  dropdownSettings = {
    singleSelection: false,
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    badgeShowLimit: 1,
  };

  private images: any = {};
  private selectedPdfObject: any[] = [];
  private checkedList: any[] = [];
  private pahafYear: string = new Date().getFullYear().toString();
  private pipe = new DatePipe('en-US');
  private username: string = '';
  
  private qualityCodingList: any[] = [];
  private qualityHeaderList: any[] = [];
  private resultHcc: any[] = [];
  private resultDx: any[] = [];
  private refreshDateN: any[] = [];
  private uniquePrevCondition: any[] = [];
  private memberMeasureDatas: any[] = [];
  private measuresListDataArray: any[] = [];
  private formDataArray: any[] = [];
  private prevCondition: any[] = [];
  private poteintialCondition: any[] = [];
  private dxDt: any[] = [];
  private hccDt: any[] = [];
  private prevFlag: boolean = true;
  private ptnFlag: boolean = true;
  private gender: string = '';
  private date: string = '';
  private refreshDate: string = '';
  private measuresListData: any[] = [];
  private qualityCodingListData: any[] = [];
  
  downloadedMembers: Set<string> = new Set();

  constructor(
    private epahafService: EpahafService
  ) {
    this.dataSource = new MatTableDataSource<PahafMemberData>([]);
    
    this.epahafService.downloadedMembers$.subscribe(downloaded => {
      this.downloadedMembers = downloaded;
    });
  }

  ngOnInit(): void {
    this.initializeDataSource();
  }

  ngOnDestroy(): void {
    this.clearCache();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['memberData'] && !changes['memberData'].firstChange) {
      this.initializeDataSource();
    }
  }

  initializeDataSource(): void {
    if (this.memberData && Array.isArray(this.memberData)) {
      const validData = this.memberData.filter(item => 
        item && typeof item === 'object' && item.memberHmoID
      );
      this.dataSource.data = validData;
      
      if (this.paginator) {
        this.dataSource.paginator = this.paginator;
      }
      if (this.sort) {
        this.dataSource.sort = this.sort;
      }
      
      this.selection.clear();
    }

    if (this.memberData && this.memberData.length) {
      this.memberData.filter((element) => {
        if (element.pcpId) {
          this.uniquePCP.push({
            id: element.pcpId,
            itemName: element.pcpName
          })
        }
      })
      this.pcpOptions = [...new Map(this.uniquePCP.map(item => [item['itemName'], item])).values()]
      //console.log("uniquePCP -- 2 dropdownPCPList :",this.pcpOptions);

    }
  }

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
      
      // Subscribe to pagination events to clear selection
      this.paginator.page.subscribe(() => {
        this.onPaginationChange();
      });
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

    /** Clear all selections when pagination changes */
  onPaginationChange(): void {
    this.selection.clear();
  }

  /** Whether the number of selected elements matches the total number of rows on current page. */
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const currentPageData = this.getCurrentPageData();
    const numCurrentPage = currentPageData.length;
    
    return numCurrentPage > 0 && currentPageData.every(row => this.selection.isSelected(row));
  }

  toggleAllRows(): void {
    if (this.isAllSelected()) {
      const currentPageData = this.getCurrentPageData();
      currentPageData.forEach(row => this.selection.deselect(row));
    } else {
      const currentPageData = this.getCurrentPageData();
      currentPageData.forEach(row => this.selection.select(row));
    }
  }

  /** Get current page data */
  getCurrentPageData(): PahafMemberData[] {
    if (!this.paginator) {
      return this.dataSource.filteredData;
    }
    
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    const endIndex = startIndex + this.paginator.pageSize;
    return this.dataSource.filteredData.slice(startIndex, endIndex);
  }


  applyFilters(): void {
    this.dataSource.filterPredicate = (data: PahafMemberData, filter: string) => {
      const filters = JSON.parse(filter);
      
      let matchesPahafStatus = true;
      let matchesRefreshStatus = true;
      let matchesPcp = true;

      if (filters.pahafStatus && filters.pahafStatus.length > 0) {
        matchesPahafStatus = filters.pahafStatus.some((status: any) => 
          data.status && data.status.toLowerCase() === status.itemName.toLowerCase()
        );
      }

      if (matchesPahafStatus && filters.refreshStatus && filters.refreshStatus.length > 0) {
        matchesRefreshStatus = filters.refreshStatus.some((status: any) => 
          data.refreshStatus && data.refreshStatus.toLowerCase() === status.itemName.toLowerCase()
        );
      }

      if (matchesPahafStatus && matchesRefreshStatus && filters.pcpName && filters.pcpName.length > 0) {
        matchesPcp = filters.pcpName.some((pcpOption: any) =>
          data.pcpName && data.pcpName.toLowerCase() === pcpOption.itemName.toLowerCase()
        );
      }

      return matchesPahafStatus && matchesRefreshStatus && matchesPcp;
    };

    const filterValue = JSON.stringify({
      pahafStatus: this.selectedPahafStatus,
      refreshStatus: this.selectedRefreshStatus,
      pcpName: this.selectedPcp
    });

    this.dataSource.filter = filterValue;
    
    this.selection.clear();
    
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  onPahafStatusChange(): void {
    const notReceivedValue = this.selectedPahafStatus.filter(item => item.id === 5);
    if (notReceivedValue.length > 0) {
      this.isSecondDropdownDisabled = false;
    } else {
      this.isSecondDropdownDisabled = true;
      this.selectedRefreshStatus = [];
    }
    this.applyFilters();
  }

  onRefreshStatusChange(): void {
    this.applyFilters();
  }

  clearFilters(): void {
    this.selectedRefreshStatus = [];
    this.selectedPahafStatus = [];
    this.isSecondDropdownDisabled = true;
    this.selectedPcp = [];
    this.applyFilters();
  }

  onPahafStatusSelect(item: any): void {
    const notReceivedValue = this.selectedPahafStatus.filter(item => item.id === 5);
    if (notReceivedValue.length > 0) {
      this.isSecondDropdownDisabled = false;
    } else {
      this.isSecondDropdownDisabled = true;
    }
    this.applyFilters();
  }

  onPahafStatusDeSelect(item: any): void {
    if (this.selectedPahafStatus.length === 0) {
      this.selectedRefreshStatus = [];
      this.isSecondDropdownDisabled = true;
    } else {
      const notReceivedValue = this.selectedPahafStatus.filter(item => item.id === 5);
      if (notReceivedValue.length > 0) {
        this.isSecondDropdownDisabled = false;
      } else {
        this.isSecondDropdownDisabled = true;
        this.selectedRefreshStatus = [];
      }
    }
    this.applyFilters();
  }

  onPahafStatusSelectAll(items: any): void {
    const notReceivedValue = this.selectedPahafStatus.filter(item => item.id === 5);
    if (notReceivedValue.length > 0) {
      this.isSecondDropdownDisabled = false;
    } else {
      this.isSecondDropdownDisabled = true;
    }
    this.applyFilters();
  }

  onPahafStatusDeSelectAll(items: any): void {
    this.selectedRefreshStatus = [];
    this.isSecondDropdownDisabled = true;
    this.applyFilters();
  }

  onRefreshStatusSelect(item: any): void {
    this.applyFilters();
  }

  onRefreshStatusDeSelect(item: any): void {
    this.applyFilters();
  }

  onRefreshStatusSelectAll(items: any): void {
    this.applyFilters();
  }

  onRefreshStatusDeSelectAll(items: any): void {
    this.applyFilters();
  }

  onPCPSelect(item: any): void {
    this.applyFilters();
  }

  onPCPDeSelect(item: any): void {
    this.applyFilters();
  }

  onPCPSelectAll(items: any): void {
    this.applyFilters();
  }

  onPCPDeSelectAll(items: any): void {
    this.applyFilters();
  }

  getSelectedCount(): number {
    return this.selection.selected.length;
  }

  downloadSelected(): void {
    const selectedData = this.selection.selected;
    //console.log('Download selected items:', selectedData);
  }

  async downloadSelectedPDFs(): Promise<void> {
    if (this.selection.selected.length === 0) {
      alert('Please select at least one member to download PDFs.');
      return;
    }

    this.isDownloading = true;
    this.selectedPdfObject = [];
    this.checkedList = this.selection.selected;

    try {
      this.username = (this.memberData[0] as any)?.userName || '';

      await this.loadImages();

      let respDataPahaf: any[] = [];
      let codingConstant: any[] = [];

      if (this.isCacheInitialized && this.cachedBulkPahafData && this.cachedCodingConstant) {
        //console.log('Using cached PAHAF data');
        respDataPahaf = this.cachedBulkPahafData;
        codingConstant = this.cachedCodingConstant;
      } else {
        //console.log('Fetching PAHAF data from API');
        
        const memberPahafData = await this.epahafService.getbulkPaHafData('', this.username).toPromise();
        //console.log('memberPahafData:', memberPahafData);
        
        memberPahafData.responseDatasArray.forEach((element) => {
          respDataPahaf.push(JSON.parse(element));
        });

        //console.log('respDataPahaf:', respDataPahaf);
        
        const memberID0 = this.checkedList.length > 0 ? this.checkedList[0].memberHmoID : '';
        const codingConstantresp: any = await this.epahafService
          .getMeasuresQualityCoding(memberID0)
          .toPromise();
        codingConstantresp.responseDatasArray.forEach((element) => {
          codingConstant = element.qualityCodingList;
        });

        this.cachedBulkPahafData = respDataPahaf;
        this.cachedCodingConstant = codingConstant;
        this.isCacheInitialized = true;
        //console.log('Data cached successfully');
      }

      //console.log('Processing selected members:', this.checkedList);

      for (const singleMember of this.checkedList) {
        await this.processMemberData(singleMember, respDataPahaf, codingConstant);
      }

      await this.downloadAsPDF();

    } catch (error) {
      console.error('Error downloading PDFs:', error);
      alert('An error occurred while generating PDFs. Please try again.');
      this.isDownloading = false;
    }
  }

  private clearCache(): void {
    this.cachedBulkPahafData = null;
    this.cachedCodingConstant = null;
    this.isCacheInitialized = false;
    //console.log('Cache cleared');
  }

  private async loadImages(): Promise<void> {
    this.images.logo = await this.getBase64ImageFromURL('assets/images/logo.png');
    this.images.systolic = await this.getBase64ImageFromURL('assets/images/systolic.png');
    this.images.cancerScreening = await this.getBase64ImageFromURL('assets/images/cancerScreening.png');
    this.images.signature = await this.getBase64ImageFromURL('assets/images/signature.png');
    this.images.alogo = await this.getBase64ImageFromURL('assets/images/A.PNG');
    this.images.dlogo = await this.getBase64ImageFromURL('assets/images/D.PNG');
    this.images.ulogo = await this.getBase64ImageFromURL('assets/images/U.PNG');
    this.images.adulogo = await this.getBase64ImageFromURL('assets/images/ADU.PNG');
    this.images.diabetes = await this.getBase64ImageFromURL('assets/images/diabetes.png');
    this.images.checkbox = await this.getBase64ImageFromURL('assets/images/checkbox.svg');
    this.images.rectBig = await this.getBase64ImageFromURL('assets/images/rectBig.svg');
    this.images.starMeasure = await this.getBase64ImageFromURL('assets/images/starMeasure.png');
  }

  private getBase64ImageFromURL(url: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };

      img.onerror = (error) => {
        reject(error);
      };
      img.src = url;
    });
  }

  private async processMemberData(singleMember: any, respDataPahaf: any[], codingConstant: any[]): Promise<void> {
    // Reset arrays for each member
    this.qualityCodingList = [];
    this.qualityHeaderList = [];
    this.resultHcc = [];
    this.resultDx = [];
    this.refreshDateN = [];
    this.uniquePrevCondition = [];
    this.memberMeasureDatas = [];
    this.measuresListDataArray = [];

    let memberPahafData: any = respDataPahaf.find(item => item[singleMember.memberHmoID]);
    memberPahafData = memberPahafData ? memberPahafData[singleMember.memberHmoID] : {};

    // Process measures list
    this.measuresListData = memberPahafData.openQualityMeasure || [];
    if (this.measuresListData) {
      this.measuresListData.forEach((rec) => {
        this.refreshDate = this.pipe.transform(rec.refreshDate, 'MM-dd-yyyy');
        this.measuresListDataArray.push(rec);
      });
      if (this.refreshDate) {
        this.refreshDateN.push(this.refreshDate);
      }
    }

    // Process quality coding list
    this.qualityCodingListData = codingConstant;
    this.qualityCodingListData.forEach((item) => {
      let quality: any = {
        LOOKUP_ID: item.qualityID,
        LOOKUP_TYPE: item.qualityType,
        LOOKUP_CODE: item.qualityCode,
        LOOKUP_DESCRIPTION: item.qualityDescription,
        LOOKUP_PARENT_ID: item.qualityParentID
      };

      if (item.qualityType === 'Quality_Header') {
        this.qualityHeaderList.push(quality);
      }
      if (item.qualityType === 'Quality_Coding') {
        this.qualityCodingList.push(quality);
      }
    });

    let duplicates = [];

    // Populate form data
    this.formDataArray = [
      {
        lastName: memberPahafData?.lastname,
        firstName: memberPahafData?.firstname,
        providerName: memberPahafData?.providerName,
        memberGender: memberPahafData?.gender,
        memberType: memberPahafData?.memberType,
        providerNetwork: memberPahafData?.pcpNetwork,
        pCPNeighborhood: memberPahafData?.pcpNeighborhood,
        memberId: singleMember.memberHmoID
      }
    ];

    // Process previous conditions
    if (memberPahafData.previous) {
      memberPahafData.previous.map(item => {
        item.dXCode = item.dxCode || null;
        item.hccId = item.hccID || null;
        item.suspectcondition = item.suspectCondition || null;
        item.moddcohort = item.moddCohort || null;
        item.pcpreportedDate = item.pcpReportedDate || null;
        item.dXDescription = item.dxDescription || null;
        item.instruction = item.instruction || null;
      });

      let filterdPrevCondition = memberPahafData.previous;
      this.prevCondition = filterdPrevCondition
        .map(function (item) {
          if (item.hccId === '108') {
            item.hccId = '18';
          }
          return item;
        })
        .sort(
          (a, b) =>
            a.memberId - b.memberId ||
            a.hccId - b.hccId ||
            a.dXCode - b.dXCode
        );

      this.prevCondition.map((e) => {
        if (!duplicates.includes(e.dXCode)) {
          this.uniquePrevCondition.push(e);
          duplicates.push(e.dXCode);
        }
      });

      const NewPRflag = this.prevCondition.filter(data => data.dXCode == null);
      this.prevFlag = NewPRflag.length === 0;
    }

    // Process potential conditions
    if (memberPahafData.potential) {
      memberPahafData.potential.map(item => {
        item.dXCode = item.dxCode || null;
        item.hccId = item.hccID || null;
        item.suspectcondition = item.suspectCondition || null;
        item.moddcohort = item.moddCohort || null;
        item.pcpreportedDate = item.pcpReportedDate || null;
        item.dXDescription = item.dxDescription || null;
        item.instruction = item.instruction || null;
      });
      this.poteintialCondition = memberPahafData.potential
      // this.poteintialCondition = memberPahafData.potential.sort(
      //   (a, b) =>
      //     (b.moddcohort &&
      //       a.moddcohort &&
      //       b.moddcohort.toUpperCase().localeCompare(a.moddcohort.toUpperCase())) ||
      //     (a.suspectcondition &&
      //       b.suspectcondition &&
      //       a.suspectcondition.localeCompare(b.suspectcondition)) ||
      //     (a.indicatorDescription &&
      //       b.indicatorDescription &&
      //       a.indicatorDescription.localeCompare(b.indicatorDescription)) ||
      //     (a.indicatorDOS &&
      //       b.indicatorDOS &&
      //       a.indicatorDOS.split('-').reverse().join('').localeCompare(b.indicatorDOS.split('-').reverse().join('')))
      // );

      this.dxDt = this.poteintialCondition.filter(dxData => dxData.healthplan === 'Navina');
      this.hccDt = this.poteintialCondition.filter(dxData => dxData.healthplan !== 'Navina');

      if (this.dxDt.length) {
        let grouped = _.groupBy(this.dxDt, 'dXCode');
        this.resultDx = Object.keys(grouped).map((key) => [
          key,
          _.uniqBy(grouped[key], 'dXCode'),
        ]);
      }

      if (this.hccDt.length) {
        let grouped = _.groupBy(this.hccDt, 'hccDescription');
        this.resultHcc = Object.keys(grouped).map((key) => [
          key,
          _.uniqBy(grouped[key], 'dXCode'),
        ]);
      }

      const NewPTflag = this.poteintialCondition.filter(data => data.dXCode == null && data.hccId == null);
      this.ptnFlag = NewPTflag.length === 0;
    }

    // Process member measures
    this.memberMeasureDatas = memberPahafData.memberMeasures || [];

    // Set gender and date
    if (memberPahafData.gender === 'F') {
      this.gender = 'Female';
    } else {
      this.gender = 'Male';
    }
    if (memberPahafData.dob) {
      this.date = memberPahafData.memberDateOfBirth;
    }

    // Create PDF object
    let pdfObj: any = {
      formDataArray: this.formDataArray,
      parentValue: singleMember,
      previousCondition: this.uniquePrevCondition,
      poteintialConditionDataDx: this.resultDx,
      poteintialConditionDataHcc: this.resultHcc,
      memberMeasureDatas: this.memberMeasureDatas,
      qualityHeaderList: this.qualityHeaderList,
      qualityCodingList: this.qualityCodingList,
      measuresListData: this.measuresListDataArray,
      refreshDate: this.refreshDateN,
      prevFlag: this.prevFlag,
      ptnFlag: this.ptnFlag,
    };

    this.selectedPdfObject.push(pdfObj);
    //console.log('Processed member:', singleMember.memberHmoID);
  }

  private async downloadAsPDF(): Promise<void> {
    const startTime = new Date();
    const chunkSize = 100;
    const totalPdfs = this.selectedPdfObject.length;

    //console.log(`Starting PDF generation for ${totalPdfs} members`);

    // Process PDFs in chunks of 100
    for (let i = 0; i < totalPdfs; i += chunkSize) {
      const chunk = this.selectedPdfObject.slice(i, i + chunkSize);
      const chunkNumber = Math.floor(i / chunkSize) + 1;
      const totalChunks = Math.ceil(totalPdfs / chunkSize);

      // Get corresponding chunk from checkedList
      const checklistChunk = this.checkedList.slice(i, i + chunkSize);

      //console.log(`Processing chunk ${chunkNumber} of ${totalChunks} (${chunk.length} PDFs)`);

      // Create a new PdfService instance for this chunk
      const pdfServiceInstance = new PdfService();

      // Process each PDF in the current chunk
      for (const [index, pdf] of chunk.entries()) {
        await pdfServiceInstance.generate(
          pdf,
          this.images,
          index,
          checklistChunk,
          this.pahafYear
        );
      }

      //console.log('Waiting 40 seconds before finalizing chunk...');
      await this.delay(6000);

      // Finalize and download the current chunk
      //await pdfServiceInstance.finalizeChunk(chunkNumber);

      // Wait 10 seconds before processing next chunk (except for the last chunk)
      if (i + chunkSize < totalPdfs) {
        //console.log('Waiting 10 seconds before next chunk...');
        await this.delay(10000);
      }
    }

    const endTime = new Date();
    const duration = endTime.getTime() - startTime.getTime();
    const seconds = duration / 1000;

    // console.log('All PDF chunks processed successfully');
    // console.log('Start Time:', startTime.toLocaleTimeString());
    // console.log('End Time:', endTime.toLocaleTimeString());
    // console.log('Total Time Taken:', duration + ' ms');
    // console.log('Total Time Taken in seconds:', seconds + ' seconds');

    const downloadedMemberIds = this.checkedList.map(member => member.memberHmoID);
    this.epahafService.markMembersAsDownloaded(downloadedMemberIds);

    this.isDownloading = false;

    //alert(`PDF generation completed! ${totalPdfs} PDFs generated in ${seconds.toFixed(2)} seconds.`);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getStatusClass(status: string): string {
    if (!status) return '';
    
    const statusMap: { [key: string]: string } = {
      'Not Received': 'status-grey',
      'Pending Coder Review': 'status-blue',
      'Payment Approved': 'status-lightGreen',
      'Paper PAHAF In Progress': 'status-line',
      'Denied': 'status-Red',
      'Portal In Progress': 'status-grey1',
      'On Hold - Missing claim': 'status-line-Red',
      'On Hold - Non-AWV Claim': 'status-line-Orange',
      'Paid': 'status-green'
    };
    
    return statusMap[status] || '';
  }

  isRowDownloaded(row: PahafMemberData): boolean {
    return this.downloadedMembers.has(row.memberHmoID);
  }
}
